import { type ConnectivityReadModel, isConnectivityReadModel } from "@billiard/contracts";

export const CONNECTIVITY_MAX_AGE_MS = 3_000;

export interface ConnectivityTrustInput {
  data: ConnectivityReadModel | undefined;
  dataUpdatedAt: number;
  isPending: boolean;
  isError: boolean;
  isRefetchError?: boolean;
  failureCount: number;
}

export type ConnectivityTrustState =
  | { kind: "LOADING" }
  | { kind: "ERROR" }
  | { kind: "STALE" }
  | { kind: "TRUSTED"; data: ConnectivityReadModel };

export function connectivityTrustState(
  input: ConnectivityTrustInput,
  nowMs = Date.now(),
  maxAgeMs = CONNECTIVITY_MAX_AGE_MS,
): ConnectivityTrustState {
  if (!input.data) {
    return input.isPending && !input.isError ? { kind: "LOADING" } : { kind: "ERROR" };
  }
  if (!isConnectivityReadModel(input.data)) {
    return { kind: "ERROR" };
  }
  if (input.isError || input.isRefetchError || input.failureCount > 0) {
    return { kind: "ERROR" };
  }
  const ageMs = nowMs - input.dataUpdatedAt;
  if (!Number.isFinite(ageMs) || ageMs < 0 || ageMs > maxAgeMs) {
    return { kind: "STALE" };
  }
  return { kind: "TRUSTED", data: input.data };
}

export function trustedConnectivity(
  input: ConnectivityTrustInput,
  nowMs = Date.now(),
): ConnectivityReadModel | undefined {
  const state = connectivityTrustState(input, nowMs);
  return state.kind === "TRUSTED" ? state.data : undefined;
}

export function requireTrustedOnlineConnectivity(
  input: ConnectivityTrustInput,
  feature: string,
  requiredService?: keyof ConnectivityReadModel["serviceHealth"],
  nowMs = Date.now(),
): ConnectivityReadModel {
  const status = trustedConnectivity(input, nowMs);
  if (!status) {
    throw new Error(`${feature}前无法确认门店与云端连接，请稍后重试`);
  }
  if (status.mode !== "ONLINE" || (requiredService && !status.serviceHealth[requiredService])) {
    throw new Error(`${feature}当前不可用，请等待门店与云端服务恢复`);
  }
  return status;
}

export function requireTrustedStoreAuthority(
  input: ConnectivityTrustInput,
  feature: string,
  nowMs = Date.now(),
): ConnectivityReadModel {
  const status = trustedConnectivity(input, nowMs);
  if (!status) {
    throw new Error(`${feature}前无法确认门店连接和离线经营权威，请稍后重试`);
  }
  if (!status.offlineAuthorityAvailable) {
    throw new Error(`${feature}当前没有有效的离线经营权威，操作已暂停`);
  }
  return status;
}
